import {ErrorHandler, Inject, Injectable, Injector} from '@angular/core';
import {addAngularError} from '@datadog/browser-rum-angular';
import {Store} from '@ngrx/store';
import {environment} from '../../../environments/environment';
import {EnvironmentConfiguration} from '../../services/core/environment-configuration.service';
import {ProductManagementError} from './product-management-error';
import {uncaughtError} from './error-handling.actions';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private _store?: Store;
  private isDispatching = false;
  private lastErrorSignature = '';
  private lastErrorAt = 0;

  constructor(@Inject(Injector) injector: Injector) {
    setTimeout(() => {
      this._store = injector.get(Store);
    });
  }

  handleError(unhandledError: unknown): void {
    if (!unhandledError) {
      return;
    }

    const error = unhandledError instanceof Error ? unhandledError : new Error(String(unhandledError));
    const message = error.message || 'Unknown error';
    const stack = error.stack;
    const signature = `${error.name ?? 'Error'}|${message}`;
    const now = Date.now();
    const isDuplicateBurst = signature === this.lastErrorSignature && now - this.lastErrorAt < 750;

    if (isDuplicateBurst || this.isDispatching) {
      return;
    }

    this.lastErrorSignature = signature;
    this.lastErrorAt = now;

    const productManagementError = new ProductManagementError(message, stack);

    if (!this._store) {
      if (!EnvironmentConfiguration.enableProdMode()) {
        // eslint-disable-next-line no-console
        console.error(productManagementError);
      }
      this.reportToDatadog(unhandledError);
      return;
    }

    this.isDispatching = true;
    try {
      this._store.dispatch(uncaughtError({error: productManagementError}));
    } finally {
      this.isDispatching = false;
    }

    this.reportToDatadog(unhandledError);
  }

  /** Reports Angular errors through the Datadog RUM Angular plugin. */
  private reportToDatadog(error: unknown): void {
    if (!environment.enableDatadogRum) {
      return;
    }

    addAngularError(error);
  }
}
